import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Pagination,
  Tooltip,
  Menu,
  useTheme,
  Breadcrumbs,
  Link,
  InputAdornment,
  Divider,
  Stack,
  Avatar
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  Edit,
  LocalShipping,
  Receipt,
  AttachMoney,
  Person,
  Store,
  CalendarToday,
  TrendingUp,
  ShoppingCart,
  CheckCircle,
  Pending,
  Cancel,
  Warning,
  Download,
  ArrowUpward,
  ArrowDownward,
  Inventory
} from '@mui/icons-material';
import { orderService } from '../../services/orderService';
import { getProductPrimaryImageUrl, getFullImageUrl } from '../../utils/imageUtils';

const AdminOrdersPage = () => {
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [orderCounts, setOrderCounts] = useState({});
  
  // Filter states - simplified to only include status, sort by, and order
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedOrderForAction, setSelectedOrderForAction] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Status configuration
  const statusConfig = {
    PENDING: { label: 'Pending', color: 'warning', icon: <Pending /> },
    CONFIRMED: { label: 'Confirmed', color: 'info', icon: <CheckCircle /> },
    PROCESSING: { label: 'Processing', color: 'primary', icon: <TrendingUp /> },
    SHIPPED: { label: 'Shipped', color: 'secondary', icon: <LocalShipping /> },
    DELIVERED: { label: 'Delivered', color: 'success', icon: <CheckCircle /> },
    CANCELLED: { label: 'Cancelled', color: 'error', icon: <Cancel /> },
    REFUNDED: { label: 'Refunded', color: 'default', icon: <AttachMoney /> }
  };

  // Helper function to get seller names from order items
  const getSellerNames = (order) => {
    if (!order.items || order.items.length === 0) {
      return 'No Seller';
    }

    // Get unique seller names from order items
    const sellerNames = [...new Set(
      order.items
        .map(item => item.sellerName || item.sellerId)
        .filter(name => name)
    )];

    if (sellerNames.length === 0) {
      return 'Unknown Seller';
    } else if (sellerNames.length === 1) {
      return sellerNames[0];
    } else {
      return `${sellerNames[0]} +${sellerNames.length - 1} more`;
    }
  };

  // Load orders with proper filter parameters
  const loadOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        size: 10,
        sortBy,
        sortDir
      };

      // Only add status filter if it's not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      console.log('Loading orders with params:', params);
      const response = await orderService.getAllOrdersForAdmin(params);
      console.log('Orders response:', response);
      
      setOrders(response.orders || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || response.totalOrders || 0);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Load order counts
  const loadOrderCounts = async () => {
    try {
      const counts = await orderService.getAdminOrderCounts();
      console.log('Order counts:', counts);
      setOrderCounts(counts);
    } catch (err) {
      console.error('Failed to load order counts:', err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [page, statusFilter, sortBy, sortDir]);

  useEffect(() => {
    loadOrderCounts();
  }, []);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
    setPage(0); // Reset to first page when sort changes
  };

  const handleSortDirChange = (event) => {
    setSortDir(event.target.value);
    setPage(0); // Reset to first page when sort direction changes
  };

  const handleHeaderSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to desc
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(0); // Reset to first page when sort changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleRefresh = () => {
    loadOrders();
    loadOrderCounts();
  };

  const handleOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleActionMenuOpen = (event, order) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedOrderForAction(order);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedOrderForAction(null);
  };

  // Invoice generation functionality
  const handleGenerateInvoice = (order) => {
    setSelectedOrder(order);
    setInvoiceDialogOpen(true);
    handleActionMenuClose();
  };

  const generateInvoiceData = (order) => {
    return {
      invoiceNumber: `INV-${order.orderNumber || order.id}`,
      orderNumber: order.orderNumber || order.id,
      date: new Date().toLocaleDateString(),
      orderDate: formatDate(order.createdAt),
      customer: {
        name: order.customerName || 'N/A',
        id: order.customerId,
        email: order.customerEmail || 'N/A'
      },
      items: order.items || [],
      subtotal: order.totalAmount || 0,
      tax: (order.totalAmount || 0) * 0.1, // Assuming 10% tax
      total: (order.totalAmount || 0) * 1.1,
      status: order.status
    };
  };

  const downloadInvoice = () => {
    if (!selectedOrder) return;

    setGeneratingInvoice(true);
    
    try {
      const invoiceData = generateInvoiceData(selectedOrder);
      
      // Create HTML content for the invoice
      const invoiceHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${invoiceData.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .invoice-details { margin-bottom: 20px; }
            .customer-details { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .total-section { text-align: right; margin-top: 20px; }
            .total-row { font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INVOICE</h1>
            <h2>${invoiceData.invoiceNumber}</h2>
          </div>
          
          <div class="invoice-details">
            <p><strong>Invoice Date:</strong> ${invoiceData.date}</p>
            <p><strong>Order Number:</strong> ${invoiceData.orderNumber}</p>
            <p><strong>Order Date:</strong> ${invoiceData.orderDate}</p>
            <p><strong>Status:</strong> ${invoiceData.status}</p>
          </div>
          
          <div class="customer-details">
            <h3>Bill To:</h3>
            <p><strong>Customer:</strong> ${invoiceData.customer.name}</p>
            <p><strong>Customer ID:</strong> ${invoiceData.customer.id}</p>
            <p><strong>Email:</strong> ${invoiceData.customer.email}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Seller</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoiceData.items.map(item => `
                <tr>
                  <td>${item.productName || item.productId}</td>
                  <td>${item.sellerName || item.sellerId}</td>
                  <td>${item.quantity}</td>
                  <td>$${(item.unitPrice || item.totalPrice / item.quantity || 0).toFixed(2)}</td>
                  <td>$${(item.totalPrice || item.unitPrice * item.quantity || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="total-section">
            <p>Subtotal: $${invoiceData.subtotal.toFixed(2)}</p>
            <p>Tax (10%): $${invoiceData.tax.toFixed(2)}</p>
            <p class="total-row">Total: $${invoiceData.total.toFixed(2)}</p>
          </div>
        </body>
        </html>
      `;

      // Create and download the file
      const blob = new Blob([invoiceHTML], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceData.invoiceNumber}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setInvoiceDialogOpen(false);
    } catch (error) {
      console.error('Error generating invoice:', error);
      setError('Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusChip = (status) => {
    const config = statusConfig[status] || { label: status, color: 'default', icon: null };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        variant="outlined"
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link color="inherit" href="/admin">
            Admin Dashboard
          </Link>
          <Typography color="text.primary">Orders Management</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Orders Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <ShoppingCart />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Orders
                  </Typography>
                  <Typography variant="h4">
                    {orderCounts.total || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <Pending />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending
                  </Typography>
                  <Typography variant="h4">
                    {orderCounts.pending || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <LocalShipping />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Shipped
                  </Typography>
                  <Typography variant="h4">
                    {orderCounts.shipped || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Delivered
                  </Typography>
                  <Typography variant="h4">
                    {orderCounts.delivered || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Simplified Filters - Only Status, Sort By, and Order */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                  <MenuItem value="PROCESSING">Processing</MenuItem>
                  <MenuItem value="SHIPPED">Shipped</MenuItem>
                  <MenuItem value="DELIVERED">Delivered</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="REFUNDED">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortByChange}
                >
                  <MenuItem value="createdAt">Date Created</MenuItem>
                  <MenuItem value="totalAmount">Total Amount</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="orderNumber">Order Number</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortDir}
                  label="Order"
                  onChange={handleSortDirChange}
                >
                  <MenuItem value="desc">Desc</MenuItem>
                  <MenuItem value="asc">Asc</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader
          title={`Orders (${totalElements} total)`}
          action={
            <Chip
              label={`Page ${page + 1} of ${totalPages}`}
              variant="outlined"
              size="small"
            />
          }
        />
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleHeaderSort('orderNumber')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Order #
                        {sortBy === 'orderNumber' && (
                          sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Seller</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleHeaderSort('status')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Status
                        {sortBy === 'status' && (
                          sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleHeaderSort('totalAmount')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Total
                        {sortBy === 'totalAmount' && (
                          sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleHeaderSort('createdAt')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Date
                        {sortBy === 'createdAt' && (
                          sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          No orders found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {order.orderNumber || order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {order.customerName || order.customerId}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {order.customerId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {getSellerNames(order)}
                            </Typography>
                            {order.items && order.items.length > 0 && (
                              <Typography variant="caption" color="textSecondary">
                                {order.items.length} item(s)
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(order.status)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(order.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOrderDetails(order)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More Actions">
                              <IconButton
                                size="small"
                                onClick={(e) => handleActionMenuOpen(e, order)}
                              >
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Order Details Dialog */}
      <Dialog
        open={orderDetailsOpen}
        onClose={() => setOrderDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt color="primary" />
            <Typography variant="h6">
              Order Details - {selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      fontWeight: 600, 
                      color: '#2C3E50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <Person fontSize="small" />
                      Customer Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>ID:</strong> {selectedOrder.customerId}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Name:</strong> {selectedOrder.customerName || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Email:</strong> {selectedOrder.customerEmail || 'N/A'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      fontWeight: 600, 
                      color: '#2C3E50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <ShoppingCart fontSize="small" />
                      Order Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Status:</strong> {getStatusChip(selectedOrder.status)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Total:</strong> {formatCurrency(selectedOrder.totalAmount)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Created:</strong> {formatDate(selectedOrder.createdAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Payment Method:</strong> {selectedOrder.paymentMethod || 'Cash on Delivery'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Payment Status:</strong> {' '}
                        <Chip 
                          size="small" 
                          label={selectedOrder.paymentStatus || 'PENDING'} 
                          color={
                            selectedOrder.paymentStatus === 'COMPLETED' ? 'success' : 
                            selectedOrder.paymentStatus === 'FAILED' ? 'error' : 
                            'warning'
                          }
                          sx={{ fontWeight: 500 }}
                        />
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ 
                        fontWeight: 600, 
                        color: '#2C3E50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}>
                        <Inventory fontSize="small" />
                        Order Items
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Seller</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrder.items.map((item, index) => {
                              // Get product image URL - prefer the order's own snapshot
                              // (productImageUrl/productImage), fall back to the live
                              // product reference (Cloudinary variant if available)
                              const snapshotImageUrl = item.productImageUrl || item.productImage;
                              const fullImageUrl = snapshotImageUrl
                                ? getFullImageUrl(snapshotImageUrl)
                                : getProductPrimaryImageUrl(item.product, false, 'medium');
                              
                              // Get product videos
                              const productVideos = item.product?.videoUrls || [];
                              
                              return (
                                <TableRow key={index} sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {fullImageUrl ? (
                                        <Box
                                          component="img"
                                          src={fullImageUrl}
                                          alt={item.productName || 'Product'}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            if (e.target.nextSibling) {
                                              e.target.nextSibling.style.display = 'flex';
                                            }
                                          }}
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                          }}
                                        />
                                      ) : null}
                                      <Box sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        bgcolor: 'grey.200', 
                                        borderRadius: 1, 
                                        display: fullImageUrl ? 'none' : 'flex',
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                      }}>
                                        <Inventory sx={{ color: 'grey.500' }} />
                                      </Box>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          {item.productName || item.productId}
                                        </Typography>
                                        {productVideos.length > 0 && (
                                          <Chip 
                                            label={`${productVideos.length} video${productVideos.length > 1 ? 's' : ''}`}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{item.sellerName || item.sellerId}</TableCell>
                                  <TableCell>{item.quantity}</TableCell>
                                  <TableCell>{formatCurrency(item.unitPrice || item.totalPrice / item.quantity || 0)}</TableCell>
                                  <TableCell>{formatCurrency(item.totalPrice || item.unitPrice * item.quantity || 0)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          p: 2
        }}>
          <Button 
            onClick={() => setOrderDetailsOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Generation Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Generate invoice for order: {selectedOrder?.orderNumber || selectedOrder?.id}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This will create a downloadable HTML invoice with all order details.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={downloadInvoice}
            variant="contained"
            startIcon={generatingInvoice ? <CircularProgress size={20} /> : <Download />}
            disabled={generatingInvoice}
          >
            {generatingInvoice ? 'Generating...' : 'Download Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu - Removed Edit Order option */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => {
          handleOrderDetails(selectedOrderForAction);
          handleActionMenuClose();
        }}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleGenerateInvoice(selectedOrderForAction)}>
          <Receipt sx={{ mr: 1 }} />
          Generate Invoice
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default AdminOrdersPage;